/**
 * Constants for filter parameter keys used in query string building and filtering
 */

/** Filter parameters for filtering jobs (assignments, categories, refinements) */
export enum FilterParam {
  OPERATOR_IDS = 'operatorIds',
  GROUP_IDS = 'groupIds',
  TAGS = 'tags',
  MY_CHATS = 'myChats',
  UNASSIGNED = 'unassigned',
  SECONDARY_OPERATOR_IDS = 'secondaryOperatorIds',
  REFINE_BY = 'refineBy',
}

/** Query parameters sent in request body (not query string) */
export enum QueryParam {
  SEARCH = 'search',
  JOB_IDS = 'jobIds',
}

/** UI state parameters (frontend-only, not sent to backend) */
export enum UIStateParamKey {
  EXPAND_INBOX_SEARCH_BAR = 'expandInboxSearchBar',
  INBOX_FILTERS = 'inboxFilters',
  UNADDRESSED_ENABLED = 'unaddressedEnabled',
}

/** Assignment entity filters (operators, groups) - checked for compression threshold */
export const ASSIGNMENT_ENTITY_PARAMS = [
  FilterParam.OPERATOR_IDS,
  FilterParam.GROUP_IDS,
] as const;

/** Categorization filters (tags) - checked for compression threshold */
export const CATEGORIZATION_PARAMS = [FilterParam.TAGS] as const;

/** Array-based filters that trigger compression when exceeding threshold */
export const ENTITY_SELECTION_PARAMS = [
  ...ASSIGNMENT_ENTITY_PARAMS,
  ...CATEGORIZATION_PARAMS,
] as const;

/** Assignment type options (stored in 'items' object) */
export const ASSIGNMENT_TYPE_OPTIONS = [
  FilterParam.MY_CHATS,
  FilterParam.UNASSIGNED,
  FilterParam.SECONDARY_OPERATOR_IDS,
] as const;

/** Refinement options (hasUnreadMessages, mentions, etc.) */
export const REFINEMENT_OPTIONS = [FilterParam.REFINE_BY] as const;

/** All filter parameters excluded from query string when compressing */
export const ALL_QUERY_FILTER_PARAMS = [
  ...ENTITY_SELECTION_PARAMS,
  ...ASSIGNMENT_TYPE_OPTIONS,
  ...REFINEMENT_OPTIONS,
] as const;

/** Parameters sent in request body instead of query string */
export const BODY_ONLY_PARAMS = [
  QueryParam.SEARCH,
  QueryParam.JOB_IDS,
] as const;

/** UI state parameters excluded from query string */
export const UI_STATE_PARAMS = [
  UIStateParamKey.EXPAND_INBOX_SEARCH_BAR,
  UIStateParamKey.INBOX_FILTERS,
  UIStateParamKey.UNADDRESSED_ENABLED,
] as const;

/** All parameters excluded from query string when building requests */
export const EXCLUDED_QUERY_PARAMS = [
  ...BODY_ONLY_PARAMS,
  ...UI_STATE_PARAMS,
] as const;

/**
 * Type definitions for filter parameter keys
 */
export type AssignmentEntityParam = (typeof ASSIGNMENT_ENTITY_PARAMS)[number];
export type CategorizationParam = (typeof CATEGORIZATION_PARAMS)[number];
export type EntitySelectionParam = (typeof ENTITY_SELECTION_PARAMS)[number];
export type AssignmentTypeOption = (typeof ASSIGNMENT_TYPE_OPTIONS)[number];
export type RefinementOption = (typeof REFINEMENT_OPTIONS)[number];
export type AllQueryFilterParam = (typeof ALL_QUERY_FILTER_PARAMS)[number];
export type BodyOnlyParam = (typeof BODY_ONLY_PARAMS)[number];
export type UIStateParam = (typeof UI_STATE_PARAMS)[number];
export type ExcludedQueryParam = (typeof EXCLUDED_QUERY_PARAMS)[number];
